import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import AdvancedTradingChart from '@/components/AdvancedTradingChart'

export default function ChartsPage() {
  const { ticker } = useParams<{ ticker: string }>()
  const { isAuthenticated, user } = useAuth()
  const [selectedTicker, setSelectedTicker] = useState(ticker || 'EURUSD')
  const [selectedTimeframe, setSelectedTimeframe] = useState('1h')

  // Popular tickers for quick access
  const popularTickers = [
    { symbol: 'EURUSD', name: 'Euro / US Dollar', category: 'forex' },
    { symbol: 'GBPUSD', name: 'British Pound / US Dollar', category: 'forex' },
    { symbol: 'USDJPY', name: 'US Dollar / Japanese Yen', category: 'forex' },
    { symbol: 'AAPL', name: 'Apple Inc', category: 'stocks' },
    { symbol: 'TSLA', name: 'Tesla Inc', category: 'stocks' },
    { symbol: 'BTCUSD', name: 'Bitcoin / US Dollar', category: 'crypto' },
  ]

  const timeframes = [
    { value: '1m', label: '1M' },
    { value: '5m', label: '5M' },
    { value: '15m', label: '15M' },
    { value: '30m', label: '30M' },
    { value: '1h', label: '1H' },
    { value: '4h', label: '4H' },
    { value: '1d', label: '1D' },
    { value: '1w', label: '1W' },
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
            {/* Ticker Selection */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Symbol</label>
                <select
                  value={selectedTicker}
                  onChange={(e) => setSelectedTicker(e.target.value)}
                  className="input w-full sm:w-40"
                >
                  {popularTickers.map((ticker) => (
                    <option key={ticker.symbol} value={ticker.symbol}>
                      {ticker.symbol}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Timeframe</label>
                <div className="flex gap-1">
                  {timeframes.map((tf) => (
                    <button
                      key={tf.value}
                      onClick={() => setSelectedTimeframe(tf.value)}
                      className={`
                        px-3 py-1 text-sm font-medium rounded transition-colors
                        ${selectedTimeframe === tf.value
                          ? 'bg-primary-600 text-white'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                        }
                      `}
                    >
                      {tf.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Chart Info */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-lg font-semibold text-foreground">
                  {popularTickers.find(t => t.symbol === selectedTicker)?.name || selectedTicker}
                </div>
                <div className="text-sm text-muted-foreground">
                  {isAuthenticated ? `${user?.tier?.toUpperCase()} Access` : 'Guest Mode'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-background rounded-lg p-4">
          <AdvancedTradingChart
            symbol={selectedTicker}
            timeframe={selectedTimeframe}
            height={600}
          />
          {!isAuthenticated && (
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Guest Mode:</strong> Sign up to access technical indicators and save layouts
              </p>
              <a href="/register" className="btn btn-primary btn-sm">
                Create free account
              </a>
            </div>
          )}
        </div>

        {/* Chart Controls */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Market Data */}
          <div className="card p-4">
            <h3 className="font-semibold text-foreground mb-3">Market Data</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Open:</span>
                <span className="font-mono">1.0892</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">High:</span>
                <span className="font-mono text-chart-up">1.0924</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Low:</span>
                <span className="font-mono text-chart-down">1.0875</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Close:</span>
                <span className="font-mono font-semibold">1.0903</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Volume:</span>
                <span className="font-mono">2.4M</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card p-4">
            <h3 className="font-semibold text-foreground mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <button className="btn btn-secondary btn-sm w-full">
                📊 Add to Watchlist
              </button>
              <button className="btn btn-secondary btn-sm w-full">
                💾 Save Layout
              </button>
              <button className="btn btn-secondary btn-sm w-full">
                📱 Share Chart
              </button>
              {!isAuthenticated && (
                <div className="text-xs text-muted-foreground mt-2">
                  Watchlist and layouts require free registration
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}